import { useState } from 'react'
import { Users, MessageCircle, Share2, Heart, MessageSquare, Search, Plus, Send, Phone, Video, Mail, Clock, MapPin } from 'lucide-react'

export default function EmployeeCollaborationPage() {
  const [activeTab, setActiveTab] = useState('directory')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState(null)

  const [employees] = useState([
    {
      id: 1,
      name: 'Admin User',
      title: 'Super Admin',
      department: 'Management',
      email: 'admin@cybertection.com',
      phone: '(555) 001-0001',
      location: 'New York, NY',
      avatar: 'https://ui-avatars.com/api/?name=Admin+User&background=0284c7&color=fff',
      status: 'online',
      bio: 'Platform Administrator',
      skills: ['Leadership', 'Security', 'AI Management'],
    },
    {
      id: 2,
      name: 'John Smith',
      title: 'Help Desk Manager',
      department: 'Support',
      email: 'john@cybertection.com',
      phone: '(555) 001-0002',
      location: 'New York, NY',
      avatar: 'https://ui-avatars.com/api/?name=John+Smith&background=0284c7&color=fff',
      status: 'online',
      bio: 'Managing support operations',
      skills: ['Customer Support', 'Ticketing', 'Training'],
    },
  ])

  const [posts] = useState([
    {
      id: 1,
      author: 'Admin User',
      department: 'Management',
      timestamp: '2 hours ago',
      content: 'Welcome to the Employee Collaboration Hub!',
      likes: 5,
      comments: 2,
      liked: false,
    },
  ])

  const [newPost, setNewPost] = useState('')

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.title.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">👥 Employee Collaboration Hub</h1>
        <p className="text-blue-200">Connect with colleagues, share updates, and collaborate</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <p className="text-blue-300/70 text-sm mb-1">Total Employees</p>
          <p className="text-3xl font-bold text-blue-400">{employees.length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
          <p className="text-green-300/70 text-sm mb-1">Online Now</p>
          <p className="text-3xl font-bold text-green-400">{employees.filter(e => e.status === 'online').length}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30 p-6">
          <p className="text-purple-300/70 text-sm mb-1">Posts</p>
          <p className="text-3xl font-bold text-purple-400">{posts.length}</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-blue-500/30">
        <button
          onClick={() => setActiveTab('directory')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'directory'
              ? 'text-blue-300 border-b-2 border-blue-500'
              : 'text-blue-400/60 hover:text-blue-300'
          }`}
        >
          Employee Directory
        </button>
        <button
          onClick={() => setActiveTab('feed')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'feed'
              ? 'text-blue-300 border-b-2 border-blue-500'
              : 'text-blue-400/60 hover:text-blue-300'
          }`}
        >
          Social Feed
        </button>
      </div>

      {activeTab === 'directory' && (
        <div className="space-y-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-3 text-blue-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredEmployees.map((employee) => (
              <div
                key={employee.id}
                className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-blue-500/30 rounded-lg p-5 hover:border-blue-500/50 cursor-pointer transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <img src={employee.avatar} alt={employee.name} className="w-12 h-12 rounded-full border border-blue-500/30" />
                    <div>
                      <p className="font-bold text-white">{employee.name}</p>
                      <p className="text-xs text-blue-300">{employee.title}</p>
                    </div>
                  </div>
                  <span className={`w-3 h-3 rounded-full ${employee.status === 'online' ? 'bg-green-400' : 'bg-yellow-400'}`} />
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-blue-300/70">
                    <Mail size={14} />
                    <span>{employee.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-blue-300/70">
                    <MapPin size={14} />
                    <span>{employee.location}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-blue-500/20 flex gap-2">
                  <button className="flex-1 px-3 py-2 bg-blue-600/30 text-blue-300 rounded-lg hover:bg-blue-600/50 text-sm">
                    Message
                  </button>
                  <button className="flex-1 px-3 py-2 bg-green-600/30 text-green-300 rounded-lg hover:bg-green-600/50 text-sm">
                    Call
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'feed' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder="Share with your team..."
              rows="4"
              className="w-full px-4 py-3 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60 resize-none"
            />
            <div className="mt-4 flex justify-end">
              <button className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-500/50 transition-all disabled:opacity-50 flex items-center gap-2">
                <Send size={18} />
                Post
              </button>
            </div>
          </div>

          {posts.map((post) => (
            <div key={post.id} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-lg border border-blue-500/30 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="font-bold text-white">{post.author}</p>
                  <p className="text-xs text-blue-300/70">{post.department} • {post.timestamp}</p>
                </div>
              </div>
              <p className="text-blue-200 mb-4">{post.content}</p>
              <div className="flex items-center gap-6 pt-4 border-t border-blue-500/20">
                <button className="flex items-center gap-2 text-blue-300/70 hover:text-red-400 transition-colors">
                  <Heart size={18} />
                  <span className="text-sm">{post.likes}</span>
                </button>
                <button className="flex items-center gap-2 text-blue-300/70 hover:text-blue-300 transition-colors">
                  <MessageCircle size={18} />
                  <span className="text-sm">{post.comments}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
